import {
  ClockIcon,
  CreditCardIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { ChannelSelector } from './ChannelSelector';
import { TemplateSection } from './TemplateSection';
import { MODAL_CONFIG, type SendMailModalProps } from './types';
import { useMailingData } from './useMailingData';

export const SendMailModal = ({
  isOpen,
  onClose,
  onConfirm,
  type,
  isLoading,
  departmentId,
  departmentName,
  policeReportId,
  matterId,
  firmId,
}: SendMailModalProps) => {
  const isFoia = type === 'foia';

  const {
    mailChannels,
    selectedChannelIdx,
    setSelectedChannelIdx,
    selectedChannel,
    templates,
    templatesLoading,
    refetchTemplates,
    selectedTemplateId,
    setSelectedTemplateId,
    matterContext,
    overrides,
    setOverrides,
    validation,
  } = useMailingData({
    isOpen,
    isFoia,
    departmentId,
    matterId,
    policeReportId,
    firmId,
  });

  if (!isOpen) return null;

  const config = MODAL_CONFIG[type];
  const Icon = type === 'cheque' ? CreditCardIcon : DocumentTextIcon;

  const isDisabled =
    isLoading ||
    (isFoia && templates.length === 0 && !templatesLoading) ||
    (isFoia && validation.missingCount > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        className="relative bg-white rounded-xl shadow-2xl border border-gray-200 max-w-3xl w-full mx-4"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
          disabled={isLoading}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full ${config.iconBg} flex items-center justify-center`}
            >
              <Icon className={`h-5 w-5 ${config.iconColor}`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {config.title}
              </h3>
              <p className="text-sm text-gray-500">{departmentName}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Unified channel/address selector */}
          <ChannelSelector
            channels={mailChannels}
            selectedIdx={selectedChannelIdx}
            onSelect={setSelectedChannelIdx}
            isFoia={isFoia}
          />

          {/* Template section */}
          <TemplateSection
            isFoia={isFoia}
            templates={templates}
            templatesLoading={templatesLoading}
            refetchTemplates={refetchTemplates}
            selectedTemplateId={selectedTemplateId}
            onTemplateChange={setSelectedTemplateId}
            matterContext={matterContext}
            overrides={overrides}
            onOverrideChange={(fieldId, value) =>
              setOverrides((prev) => ({ ...prev, [fieldId]: value }))
            }
            validation={validation}
            firmId={firmId}
          />

          {/* Info notices */}
          <div className="space-y-2">
            <p className="text-xs text-gray-600">{config.message}</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <ClockIcon className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-900">
                  <span className="font-medium">Note:</span> It may take 1-2
                  minutes for the {isFoia ? 'letter' : 'cheque'} to appear.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onConfirm(selectedChannel?.id, selectedTemplateId, overrides)
            }
            disabled={isDisabled}
            className={`flex-1 px-4 py-2 ${config.buttonBg} text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2`}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <EnvelopeIcon className="h-4 w-4" />
                <span>Send {isFoia ? 'Letter' : 'Cheque'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
